import { Cloud, Sun, Wind, Droplets, Eye } from "lucide-react";
import { Link } from "react-router-dom";

const getScoreColor = (score) => {
  if (score >= 8) return "text-emerald-400";
  if (score >= 6) return "text-amber-400";
  if (score >= 4) return "text-orange-400";
  return "text-ignition-red";
};

const getScoreBg = (score) => {
  if (score >= 8) return "bg-emerald-500/20 border-emerald-500/30";
  if (score >= 6) return "bg-amber-500/20 border-amber-500/30";
  if (score >= 4) return "bg-orange-500/20 border-orange-500/30";
  return "bg-ignition-red/20 border-ignition-red/30";
};

export const WeatherWidget = ({ weather, compact = false }) => {
  if (!weather) return null;

  const { current, viewing_score, viewing_quality, location } = weather;

  if (compact) {
    return (
      <Link to="/weather" className="block" data-testid="weather-widget-compact">
        <div className="glass-card-hover p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-widest font-heading mb-1">
                Launch Viewing
              </p>
              <p className={`text-2xl font-bold font-mono ${getScoreColor(viewing_score)}`}>
                {viewing_score}/10
              </p>
              <p className="text-sm text-slate-400">{viewing_quality}</p>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-white font-mono">
                {Math.round(current?.temperature || 0)}°
              </p>
              <p className="text-xs text-slate-500">{location}</p>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <div className="glass-card p-6" data-testid="weather-widget">
      <h3 className="section-title mb-4">Weather Conditions</h3>
      
      {/* Viewing Score */}
      <div className={`rounded-xl p-4 mb-4 border ${getScoreBg(viewing_score)}`}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-slate-400 uppercase tracking-widest font-heading">
              Launch Viewing Score
            </p>
            <p className={`text-4xl font-bold font-mono ${getScoreColor(viewing_score)}`}>
              {viewing_score}/10
            </p>
          </div>
          <div className="text-right">
            <p className={`text-xl font-heading font-bold ${getScoreColor(viewing_score)}`}>
              {viewing_quality}
            </p>
          </div>
        </div>
      </div>

      {/* Current Conditions */}
      <div className="grid grid-cols-2 gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-amber-500/20">
            <Sun className="w-5 h-5 text-amber-400" strokeWidth={1.5} />
          </div>
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wider">Temp</p>
            <p className="text-lg font-mono text-white">{Math.round(current?.temperature || 0)}°F</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-slate-500/20">
            <Cloud className="w-5 h-5 text-slate-400" strokeWidth={1.5} />
          </div>
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wider">Clouds</p>
            <p className="text-lg font-mono text-white">{current?.cloud_cover || 0}%</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-cosmic-blue/20">
            <Wind className="w-5 h-5 text-cosmic-blue" strokeWidth={1.5} />
          </div>
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wider">Wind</p>
            <p className="text-lg font-mono text-white">{Math.round(current?.wind_speed || 0)} mph</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-cyan-500/20">
            <Eye className="w-5 h-5 text-cyan-400" strokeWidth={1.5} />
          </div>
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wider">Visibility</p>
            <p className="text-lg font-mono text-white">{Math.round(current?.visibility_km || 0)} km</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export const WeatherWidgetSkeleton = () => (
  <div className="glass-card p-6">
    <div className="skeleton h-6 w-40 rounded mb-4" />
    <div className="skeleton h-24 w-full rounded-xl mb-4" />
    <div className="grid grid-cols-2 gap-4">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="flex items-center gap-3">
          <div className="skeleton w-10 h-10 rounded-lg" />
          <div className="space-y-1">
            <div className="skeleton h-3 w-12 rounded" />
            <div className="skeleton h-5 w-16 rounded" />
          </div>
        </div>
      ))}
    </div>
  </div>
);
